import { Tbody, Td, Text, Tr } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import {
  getColumnMeta,
  getColumnSizeCssVar,
  getCommonPinningStyles,
  getRowInteractionKey,
} from "@src/components/ui-kit/DataTable/Body/helpers";
import type { BodyRow } from "@src/components/ui-kit/DataTable/types";
import {
  flexRender,
  type Cell as TanstackCellType,
  type Row as TanstackRowType,
  type Table as TanstackTableType,
} from "@tanstack/react-table";
import { observer } from "mobx-react-lite";
import { memo, type ReactNode } from "react";

type TableBodyCommonProps<Row extends BodyRow> = {
  cell: TanstackCellType<Row, unknown>;
  bodyCellRenderKey: readonly unknown[];
  firstVisibleDataColumnId?: string;
  rowOriginal: Row;
  rowInteractionKey: string;
  visibleColumnIds: string[];
};

interface TableCellContentProps<Row extends BodyRow>
  extends Omit<
    TableBodyCommonProps<Row>,
    "firstVisibleDataColumnId" | "visibleColumnIds"
  > {}
const TableCellContent = observer(function BodyTableCellContent<
  Row extends BodyRow,
>({ cell }: TableCellContentProps<Row>) {
  const cellRenderer = cell.column.columnDef.cell;

  if (typeof cellRenderer === "function") {
    return cellRenderer(cell.getContext());
  }

  return flexRender(cellRenderer, cell.getContext());
});

interface TableCellProps<Row extends BodyRow>
  extends Omit<TableBodyCommonProps<Row>, "visibleColumnIds"> {
  rowDepth: number;
}
const TableCell = <Row extends BodyRow>({
  cell,
  bodyCellRenderKey,
  firstVisibleDataColumnId,
  rowInteractionKey,
  rowOriginal,
  rowDepth,
}: TableCellProps<Row>) => {
  const isFirstDataCell =
    firstVisibleDataColumnId !== undefined &&
    cell.column.id === firstVisibleDataColumnId;
  const cellMeta = getColumnMeta(cell.column.columnDef);
  const columnSizeCssVar = getColumnSizeCssVar(cell.column.id);
  const pinningStyles = getCommonPinningStyles(cell.column);

  return (
    <Td
      overflow="hidden"
      w={`calc(var(${columnSizeCssVar}) * 1px)`}
      minW={`calc(var(${columnSizeCssVar}) * 1px)`}
      maxW={`calc(var(${columnSizeCssVar}) * 1px)`}
      py={cellMeta?.padding ?? "2"}
      pr="3"
      pl={isFirstDataCell ? 3 + rowDepth * 5 : 3}
      textAlign={cellMeta?.align}
      bg={cellMeta?.backgroundColor ?? (rowDepth > 0 ? "gray.50" : "white")}
      whiteSpace="nowrap"
      textOverflow="ellipsis"
      style={pinningStyles}
    >
      <TableCellContent
        cell={cell}
        bodyCellRenderKey={bodyCellRenderKey}
        rowInteractionKey={rowInteractionKey}
        rowOriginal={rowOriginal}
      />
    </Td>
  );
};

interface TableVisibleCellRendererProps<Row extends BodyRow>
  extends Omit<TableBodyCommonProps<Row>, "rowOriginal" | "cell"> {
  row: TanstackRowType<Row>;
}
const TableVisibleCellsRenderer = <Row extends BodyRow>({
  row,
  bodyCellRenderKey,
  firstVisibleDataColumnId,
  rowInteractionKey,
}: TableVisibleCellRendererProps<Row>) => {
  const visibleCells = [
    ...row.getLeftVisibleCells(),
    ...row.getCenterVisibleCells(),
    ...row.getRightVisibleCells(),
  ];

  return visibleCells.map((cell) => {
    const isPinned = cell.column.getIsPinned();

    return (
      <TableCell
        key={cell.id}
        cell={cell}
        bodyCellRenderKey={bodyCellRenderKey}
        firstVisibleDataColumnId={
          isPinned === "left" ? undefined : firstVisibleDataColumnId
        }
        rowInteractionKey={rowInteractionKey}
        rowOriginal={row.original}
        rowDepth={row.depth}
      />
    );
  });
};

interface TableBodyRowInnerProps<Row extends BodyRow>
  extends Omit<TableBodyCommonProps<Row>, "cell"> {
  row: TanstackRowType<Row>;
  bodyCellLayoutKey: string;
}
const TableBodyRowInner = <Row extends BodyRow>({
  row,
  bodyCellRenderKey,
  firstVisibleDataColumnId,
  rowInteractionKey,
  visibleColumnIds,
}: TableBodyRowInnerProps<Row>) => {
  return (
    <Tr
      key={row.id}
      bg={row.depth > 0 ? "gray.50" : "white"}
      _hover={{
        td: {
          bg: row.depth > 0 ? "gray.100" : "gray.50",
        },
      }}
      role="group"
    >
      <TableVisibleCellsRenderer
        row={row}
        bodyCellRenderKey={bodyCellRenderKey}
        firstVisibleDataColumnId={firstVisibleDataColumnId}
        rowInteractionKey={rowInteractionKey}
        visibleColumnIds={visibleColumnIds}
      />
    </Tr>
  );
};

const TableBodyRow = memo(
  TableBodyRowInner,
  <Row extends BodyRow>(
    prev: Readonly<TableBodyRowInnerProps<Row>>,
    next: Readonly<TableBodyRowInnerProps<Row>>,
  ): boolean =>
    prev.row === next.row &&
    prev.rowOriginal === next.rowOriginal &&
    prev.bodyCellLayoutKey === next.bodyCellLayoutKey &&
    prev.bodyCellRenderKey === next.bodyCellRenderKey &&
    prev.firstVisibleDataColumnId === next.firstVisibleDataColumnId &&
    prev.rowInteractionKey === next.rowInteractionKey,
) as typeof TableBodyRowInner;

interface TableBodyRowRendererProps<Row extends BodyRow>
  extends Omit<
    TableBodyCommonProps<Row>,
    "cell" | "rowOriginal" | "rowInteractionKey"
  > {
  rows: TanstackRowType<Row>[];
  bodyCellLayoutKey: string;
  bodyInteractionStateKey: string;
}
const TableBodyRowRenderer = <Row extends BodyRow>({
  rows,
  bodyCellLayoutKey,
  bodyInteractionStateKey,
  bodyCellRenderKey,
  firstVisibleDataColumnId,
  visibleColumnIds,
}: TableBodyRowRendererProps<Row>) => {
  return (
    <Tbody>
      {rows.map((row) => (
        <TableBodyRow
          key={row.id}
          row={row}
          rowOriginal={row.original}
          bodyCellLayoutKey={bodyCellLayoutKey}
          bodyCellRenderKey={bodyCellRenderKey}
          firstVisibleDataColumnId={firstVisibleDataColumnId}
          rowInteractionKey={`${bodyInteractionStateKey}::${getRowInteractionKey(
            row,
          )}`}
          visibleColumnIds={visibleColumnIds}
        />
      ))}
    </Tbody>
  );
};

interface TableBodyProps<Row extends BodyRow>
  extends Omit<
    TableBodyCommonProps<Row>,
    "cell" | "rowOriginal" | "rowInteractionKey" | "visibleColumnIds"
  > {
  table: TanstackTableType<Row>;
  bodyCellLayoutKey: string;
  bodyInteractionStateKey: string;
  emptyState?: ReactNode;
  showOnlySubRows?: boolean;
}
export const TableBody = observer(function BodyTableBody<Row extends BodyRow>({
  table,
  bodyCellLayoutKey,
  bodyInteractionStateKey,
  bodyCellRenderKey,
  emptyState,
  firstVisibleDataColumnId,
  showOnlySubRows = false,
}: TableBodyProps<Row>) {
  const rows = table.getRowModel().rows;
  const tableRows = showOnlySubRows
    ? rows.filter((row) => row.depth > 0)
    : rows;
  const visibleColumnIds = table
    .getVisibleLeafColumns()
    .map((column) => column.id);
  const visibleColumnsCount = visibleColumnIds.length;

  if (rows.length === 0) {
    return (
      <Tbody>
        <Tr>
          <Td py="12" colSpan={visibleColumnsCount}>
            {emptyState ?? (
              <Text color="gray.500" textAlign="center">
                <Trans>No data to display.</Trans>
              </Text>
            )}
          </Td>
        </Tr>
      </Tbody>
    );
  }

  return (
    <TableBodyRowRenderer
      rows={tableRows}
      bodyCellLayoutKey={bodyCellLayoutKey}
      bodyInteractionStateKey={bodyInteractionStateKey}
      bodyCellRenderKey={bodyCellRenderKey}
      firstVisibleDataColumnId={firstVisibleDataColumnId}
      visibleColumnIds={visibleColumnIds}
    />
  );
});
